// src/components/landing/CourseGroupsSection.jsx
import React, { useContext } from 'react';
import { Box, Typography, Grid, Button, Chip, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../contexts/ThemeContext';
import { COURSE_GROUPS } from '../../data/courseGroupsData';

// Material UI Icons (NO EMOJIS)
import MemoryIcon from '@mui/icons-material/Memory';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import PaletteIcon from '@mui/icons-material/Palette';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const ICON_MAP = {
  Memory: MemoryIcon,
  Security: SecurityIcon,
  BusinessCenter: BusinessCenterIcon,
  Palette: PaletteIcon,
  Psychology: PsychologyIcon,
};

export default function CourseGroupsSection() {
  const { mode } = useContext(ThemeContext);
  const isDark = mode === 'dark';
  const navigate = useNavigate();

  return (
    <Box
      id="course-groups"
      sx={{
        bgcolor: isDark ? '#080d1a' : '#f1f5f9',
        color: isDark ? '#fff' : '#0f172a',
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        transition: 'background-color 0.3s ease, color 0.3s ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          height: '100%',
          background: isDark
            ? 'radial-gradient(circle at 50% 15%, rgba(56, 189, 248, 0.12) 0%, transparent 65%)'
            : 'radial-gradient(circle at 50% 15%, rgba(29, 108, 240, 0.08) 0%, transparent 65%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 7, maxWidth: 840, mx: 'auto', position: 'relative', zIndex: 1 }}>
        <Chip
          icon={<SchoolIcon sx={{ fontSize: '1rem !important', color: isDark ? '#38bdf8' : '#1d6cf0' }} />}
          label="SPECIALIZED ACADEMIC CURRICULUM"
          sx={{
            bgcolor: isDark ? 'rgba(56, 189, 248, 0.12)' : 'rgba(29, 108, 240, 0.1)',
            color: isDark ? '#38bdf8' : '#1d6cf0',
            border: isDark ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid rgba(29, 108, 240, 0.25)',
            fontWeight: 800,
            fontSize: '0.72rem',
            letterSpacing: 1.5,
            mb: 2,
            px: 1,
          }}
        />

        <Typography
          sx={{
            fontSize: { xs: '1.8rem', md: '2.6rem' },
            fontWeight: 900,
            letterSpacing: '-0.5px',
            color: isDark ? '#ffffff' : '#0f172a',
            lineHeight: 1.25,
            mb: 2,
          }}
        >
          Explore Our Specialized <Box component="span" sx={{ color: isDark ? '#38bdf8' : '#1d6cf0' }}>School Groups</Box>
        </Typography>

        <Typography
          sx={{
            color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#475569',
            fontSize: { xs: '0.95rem', md: '1.05rem' },
            lineHeight: 1.75,
          }}
        >
          Explore 50+ government-approved Diploma and Certificate courses organized across 5 specialized technology & business schools.
        </Typography>
      </Box>

      <Grid container spacing={3.5} sx={{ maxWidth: 1280, mx: 'auto', position: 'relative', zIndex: 1 }}>
        {COURSE_GROUPS.map((group, index) => {
          const IconComponent = ICON_MAP[group.icon] || SchoolIcon;
          const totalCourses = group.categories.reduce((sum, cat) => sum + cat.courses.length, 0);

          return (
            <Grid item xs={12} md={6} lg={4} key={group.id}>
              <motion.div
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                style={{ height: '100%' }}
              >
                <Box
                  sx={{
                    bgcolor: isDark ? 'rgba(15, 23, 42, 0.85)' : '#ffffff',
                    backdropFilter: 'blur(16px)',
                    borderRadius: 4,
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
                    boxShadow: isDark
                      ? '0 10px 30px rgba(0, 0, 0, 0.4)'
                      : '0 10px 30px rgba(29, 108, 240, 0.06)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      borderColor: group.accentColor,
                      boxShadow: isDark
                        ? `0 20px 40px -15px ${group.borderGlow}`
                        : `0 20px 35px -10px ${group.borderGlow}`,
                      '& .group-img': {
                        transform: 'scale(1.06)',
                      },
                      '& .group-arrow': {
                        transform: 'translateX(4px)',
                      },
                    },
                  }}
                >
                  {/* Banner Image Container */}
                  <Box sx={{ height: 210, position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
                    <Box
                      component="img"
                      src={group.image}
                      alt={group.shortTitle}
                      className="group-img"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        transition: 'transform 0.5s ease',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.85) 100%)',
                      }}
                    />

                    {/* Top Badges */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ position: 'absolute', top: 14, left: 14, right: 14, zIndex: 2 }}
                    >
                      <Chip
                        label={`GROUP ${group.groupNumber}`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(0, 0, 0, 0.65)',
                          backdropFilter: 'blur(8px)',
                          color: '#fff',
                          fontWeight: 800,
                          fontSize: '0.68rem',
                          letterSpacing: 1,
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                      />
                      <Chip
                        label={group.badge}
                        size="small"
                        sx={{
                          bgcolor: group.accentColor,
                          color: '#fff',
                          fontWeight: 800,
                          fontSize: '0.68rem',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        }}
                      />
                    </Stack>

                    {/* School Title over Banner */}
                    <Box sx={{ position: 'absolute', bottom: 14, left: 16, right: 16, zIndex: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            backdropFilter: 'blur(6px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            flexShrink: 0,
                          }}
                        >
                          <IconComponent sx={{ fontSize: 20 }} />
                        </Box>
                        <Typography sx={{ color: '#ffffff', fontSize: '1.05rem', fontWeight: 800, lineHeight: 1.25 }}>
                          {group.shortTitle}
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>

                  {/* Card Content Body */}
                  <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Typography
                      sx={{
                        color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#475569',
                        fontSize: '0.86rem',
                        lineHeight: 1.65,
                        mb: 2.5,
                      }}
                    >
                      {group.description}
                    </Typography>

                    {/* Core Focus Highlights */}
                    <Box sx={{ mb: 3, flex: 1 }}>
                      <Typography
                        sx={{
                          color: isDark ? 'rgba(255, 255, 255, 0.45)' : '#94a3b8',
                          fontSize: '0.68rem',
                          fontWeight: 800,
                          letterSpacing: 1,
                          textTransform: 'uppercase',
                          mb: 1.25,
                        }}
                      >
                        Core Specializations
                      </Typography>
                      <Stack spacing={0.8}>
                        {group.keyHighlights.map((item, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: '0.95rem', color: group.accentColor, flexShrink: 0 }} />
                            <Typography
                              sx={{
                                color: isDark ? '#e2e8f0' : '#334155',
                                fontSize: '0.82rem',
                                fontWeight: 600,
                              }}
                            >
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>

                    {/* Footer Row */}
                    <Box
                      sx={{
                        pt: 2.2,
                        borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box>
                        <Typography sx={{ color: group.accentColor, fontSize: '1.15rem', fontWeight: 900, lineHeight: 1 }}>
                          {totalCourses}+
                        </Typography>
                        <Typography
                          sx={{
                            color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#64748b',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            mt: 0.3,
                          }}
                        >
                          Available Courses
                        </Typography>
                      </Box>

                      <Button
                        onClick={() => navigate(`/course-groups?group=${group.id}`)}
                        variant="contained"
                        endIcon={<ArrowForwardIcon className="group-arrow" sx={{ transition: 'transform 0.2s ease' }} />}
                        sx={{
                          bgcolor: group.accentColor,
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          textTransform: 'none',
                          borderRadius: 2.5,
                          px: 2.2,
                          py: 0.85,
                          boxShadow: `0 4px 12px ${group.borderGlow}`,
                          '&:hover': {
                            bgcolor: group.accentColor,
                            filter: 'brightness(1.12)',
                          },
                        }}
                      >
                        Explore Courses
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
